// lib/geminiImageFailure.ts
import type { ImageGenerationFailureDetails } from '#types/types';
const messages = {
  blocked: 'The model blocked image generation. Review the prompt or reference photos before retrying.',
  textOnly: 'The model returned text instead of an image. Retry explicitly or try different reference photos.',
  empty: 'The model returned no candidates. Retry explicitly.',
  noImage: 'The model finished without producing an image and gave no further explanation.',
  unknown: 'The model returned no image and did not provide a known reason. Retry explicitly or try different reference photos.',
} as const;
const filterReasons = new Set(['SAFETY', 'RECITATION', 'BLOCKLIST', 'PROHIBITED_CONTENT', 'SPII', 'IMAGE_SAFETY', 'IMAGE_PROHIBITED_CONTENT', 'IMAGE_RECITATION']);
const finishReasons = new Set([...filterReasons, 'STOP', 'MAX_TOKENS', 'OTHER', 'FINISH_REASON_UNSPECIFIED', 'MALFORMED_FUNCTION_CALL', 'UNEXPECTED_TOOL_CALL', 'NO_IMAGE', 'IMAGE_OTHER']);
const blockReasons = new Set(['SAFETY', 'OTHER', 'BLOCKLIST', 'PROHIBITED_CONTENT', 'IMAGE_SAFETY']);
const providerCodes = new Set(['invalid_request_error', 'invalid_argument', 'bad_request', 'InvalidArgument', 'BadRequest', 'content_filter', 'content_policy_violation', 'content_safety_violation', 'ResponsibleAIPolicyViolation', 'DeploymentNotFound', 'invalid_api_key', 'unsupported_content_type', 'invalid_image', 'image_too_large', 'context_length_exceeded', 'rate_limit_exceeded', 'server_error']);
const providerParameters = new Set(['prompt', 'image', 'mask', 'model', 'width', 'height', 'size', 'n', 'response_format', 'api-version']);
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {};

/** One allowlist for transport, persisted records and agent context; never copy provider text. */
export function readImageGenerationFailureDetails(value: unknown): ImageGenerationFailureDetails | undefined {
  const outer = record(value);
  const data = record(outer.failureDetails ?? record(outer.responseData).failureDetails ?? record(record(outer.response).data).failureDetails);
  if (typeof data.stage !== 'string' || typeof data.reason !== 'string'
    || !['configuration', 'reference', 'provider', 'response', 'output-fetch', 'output-validation', 'output-save'].includes(data.stage)
    || !['blocked', 'no-image', 'authentication', 'rate-limit', 'invalid-request', 'unavailable', 'timeout', 'cancelled', 'invalid-response', 'unknown'].includes(data.reason)) return undefined;
  const result: ImageGenerationFailureDetails = { stage: data.stage as ImageGenerationFailureDetails['stage'], reason: data.reason as ImageGenerationFailureDetails['reason'] };
  if (typeof data.providerCode === 'string' && providerCodes.has(data.providerCode)) result.providerCode = data.providerCode;
  if (typeof data.parameter === 'string' && providerParameters.has(data.parameter)) result.parameter = data.parameter;
  if (Number.isInteger(data.httpStatus) && Number(data.httpStatus) >= 100 && Number(data.httpStatus) <= 599) result.httpStatus = Number(data.httpStatus);
  if (typeof data.responseKind === 'string' && ['blocked', 'textOnly', 'empty', 'noImage', 'unknown'].includes(data.responseKind)) result.responseKind = data.responseKind as ImageGenerationFailureDetails['responseKind'];
  if (typeof data.promptBlockReason === 'string') result.promptBlockReason = blockReasons.has(data.promptBlockReason) ? data.promptBlockReason : 'UNKNOWN';
  if (typeof data.finishReasons === 'string') result.finishReasons = [...new Set(data.finishReasons.slice(0, 2048).split(',').slice(0, 16).map(reason => finishReasons.has(reason) ? reason : 'UNKNOWN'))].join(',');
  for (const key of ['candidateCount', 'textPartCount', 'imageCount'] as const) {
    if (Number.isInteger(data[key]) && Number(data[key]) >= 0 && Number(data[key]) <= 1_000_000) result[key] = Number(data[key]);
  }
  return result;
}
export function geminiImageFailure(response: unknown, imageCount: number) {
  const body = record(response);
  const candidates = Array.isArray(body.candidates) ? body.candidates : [];
  const block = record(body.promptFeedback).blockReason;
  const promptBlockReason = typeof block === 'string' && blockReasons.has(block) ? block : 'UNKNOWN';
  const reasons = candidates.map(candidate => {
    const reason = record(candidate).finishReason;
    return typeof reason === 'string' && finishReasons.has(reason) ? reason : 'UNKNOWN';
  });
  const blocked = promptBlockReason !== 'UNKNOWN' || (!imageCount && reasons.some(reason => filterReasons.has(reason)));
  // An explicit prompt block takes precedence; otherwise image-bearing responses keep succeeding.
  if (!blocked && imageCount > 0) return null;
  const textPartCount = candidates.reduce((count: number, candidate) => {
    const parts = record(record(candidate).content).parts;
    return count + (Array.isArray(parts) ? parts.filter(part => typeof record(part).text === 'string').length : 0);
  }, 0);
  const malformed = !response || typeof response !== 'object' || (body.candidates !== undefined && !Array.isArray(body.candidates));
  const kind: NonNullable<ImageGenerationFailureDetails['responseKind']> = blocked ? 'blocked' : malformed ? 'unknown' : !candidates.length ? 'empty'
    : reasons.some(reason => reason === 'IMAGE_OTHER' || reason === 'NO_IMAGE') ? 'noImage'
    : textPartCount > 0 && reasons.every(reason => reason === 'STOP') ? 'textOnly' : 'unknown';
  return {
    message: messages[kind],
    blocked,
    code: kind === 'blocked' ? 'IMAGE_GENERATION_BLOCKED' as const
      : !body.error && block == null && (kind === 'noImage' || kind === 'textOnly' || (kind === 'empty' && Array.isArray(body.candidates))) ? 'IMAGE_GENERATION_NO_IMAGE' as const : undefined,
    diagnostics: { kind, promptBlockReason, candidateCount: candidates.length, finishReasons: [...new Set(reasons)].slice(0, 16).join(','), textPartCount, imageCount },
  };
}

export class ImageGenerationResponseError extends Error {
  readonly code;
  readonly generationBlocked;
  readonly failureDetails;
  constructor(code?: 'IMAGE_GENERATION_BLOCKED' | 'IMAGE_GENERATION_NO_IMAGE', message?: string, failureDetails?: ImageGenerationFailureDetails) {
    super(message ?? (code === 'IMAGE_GENERATION_BLOCKED' ? messages.blocked : code === 'IMAGE_GENERATION_NO_IMAGE' ? messages.noImage : messages.unknown));
    this.name = 'ImageGenerationResponseError';
    this.code = code;
    this.generationBlocked = code === 'IMAGE_GENERATION_BLOCKED';
    this.failureDetails = readImageGenerationFailureDetails({ failureDetails: failureDetails ?? { stage: 'response', reason: code === 'IMAGE_GENERATION_BLOCKED' ? 'blocked' : code === 'IMAGE_GENERATION_NO_IMAGE' ? 'no-image' : 'unknown' } });
  }
}

export class GeminiImageResponseError extends ImageGenerationResponseError {
  constructor(failure: NonNullable<ReturnType<typeof geminiImageFailure>>) {
    super(failure.code, failure.message, { stage: 'response', reason: failure.blocked ? 'blocked' : failure.code === 'IMAGE_GENERATION_NO_IMAGE' ? 'no-image' : 'unknown',
      ...failure.diagnostics, responseKind: failure.diagnostics.kind });
    this.name = 'GeminiImageResponseError';
  }
}

/** Capture a safe failure at the private provider boundary without retaining its cause or body. */
export function imageGenerationFailureAtStage(error: unknown, stage: ImageGenerationFailureDetails['stage'], httpStatus?: number, response?: unknown) {
  if (error instanceof ImageGenerationResponseError) return error;
  let body = response;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { body = undefined; }
  }
  const fields = record(error);
  const providerError = record(record(body).error);
  const innerError = record(providerError.innererror ?? providerError.inner_error);
  const providerCode = [innerError.code, providerError.code, record(body).code].find(code => typeof code === 'string' && providerCodes.has(code));
  const parameter = [innerError.param, innerError.target, providerError.param, providerError.target].find(param => typeof param === 'string' && providerParameters.has(param));
  const blocked = fields.generationBlocked === true || emptyImageGenerationResponseError(body).generationBlocked;
  const candidateStatus = httpStatus ?? fields.status ?? record(fields.response).status;
  const status = typeof candidateStatus === 'number' && Number.isInteger(candidateStatus) && candidateStatus >= 100 && candidateStatus <= 599 ? candidateStatus : undefined;
  const reason: ImageGenerationFailureDetails['reason'] = blocked ? 'blocked'
    : fields.name === 'AbortError' ? 'cancelled'
    : fields.name === 'TimeoutError' || fields.code === 'ETIMEDOUT' || fields.code === 'ECONNABORTED' ? 'timeout'
    : stage === 'provider' && (status === 401 || status === 403) ? 'authentication'
    : stage === 'provider' && status === 429 ? 'rate-limit'
    : stage === 'provider' && typeof status === 'number' && status >= 500 ? 'unavailable'
    : stage === 'provider' && typeof status === 'number' && status >= 400 ? 'invalid-request'
    : stage === 'response' ? 'invalid-response' : 'unknown';
  const stageMessages = {
    configuration: 'Image generation configuration is unavailable.',
    reference: 'The selected image references could not be prepared. Check access and supported formats.',
    provider: 'The image provider request failed without a known cause.',
    response: 'The image provider returned an unreadable or invalid response.',
    'output-fetch': 'The generated image could not be retrieved. Check its saved status before generating again.',
    'output-validation': 'The returned image did not meet the required output format.',
    'output-save': 'The generated image could not be saved. Recover the existing result before generating again.',
  };
  const reasonMessages = {
    blocked: messages.blocked,
    authentication: 'The image provider rejected access. Check the provider credentials and deployment permissions.',
    'rate-limit': 'The image provider is temporarily rate limited.',
    'invalid-request': 'The image provider rejected this request. Your brief and references are kept.',
    unavailable: 'The image provider is temporarily unavailable.',
    timeout: 'The image request timed out. Check its saved status before generating again.',
    cancelled: 'The image request was cancelled.',
  };
  return new ImageGenerationResponseError(blocked ? 'IMAGE_GENERATION_BLOCKED' : undefined,
    reasonMessages[reason as keyof typeof reasonMessages] ?? stageMessages[stage],
    { stage, reason, ...(typeof status === 'number' ? { httpStatus: status } : {}),
      ...(typeof providerCode === 'string' ? { providerCode } : {}), ...(typeof parameter === 'string' ? { parameter } : {}) });
}

export function isImageGenerationSafetyResponse(response: unknown): boolean {
  const safetyCodes = new Set(['content_filter', 'content_policy_violation', 'content_safety_violation', 'ResponsibleAIPolicyViolation']);
  const blocked = (value: unknown, depth = 0): boolean => {
    if (depth > 6 || !value || typeof value !== 'object') return false;
    if (Array.isArray(value)) return value.some(item => blocked(item, depth + 1));
    const fields = record(value);
    return fields.is_image_safe === false || fields.filtered === true || safetyCodes.has(String(fields.code))
      || (Array.isArray(fields.has_nsfw_concepts) && fields.has_nsfw_concepts.includes(true))
      || Object.values(fields).some(item => blocked(item, depth + 1));
  };
  return blocked(response);
}

/** Only call after a synchronous endpoint or SDK has completed, and no image was extracted. */
export function emptyImageGenerationResponseError(response: unknown, collection?: 'data' | 'images', terminal = true) {
  const body = record(response);
  if (isImageGenerationSafetyResponse(response)) return new ImageGenerationResponseError('IMAGE_GENERATION_BLOCKED');
  const items = collection ? body[collection] : response;
  const status = typeof body.status === 'string' ? body.status.toLowerCase() : undefined;
  const complete = !status || ['succeeded', 'completed', 'success'].includes(status);
  const confirmedEmpty = terminal && complete && !body.error && Array.isArray(items) && items.length === 0;
  return new ImageGenerationResponseError(confirmedEmpty ? 'IMAGE_GENERATION_NO_IMAGE' : undefined);
}

/** Recognize only our structured terminal outcomes, never arbitrary exception text. */
export function terminalImageGenerationFailureCode(error: unknown) {
  const body = record(error);
  const response = record(body.response);
  const codes = [body.code, body.failureCode, record(body.responseData).code, record(response.data).code];
  return codes.includes('IMAGE_GENERATION_BLOCKED') ? 'IMAGE_GENERATION_BLOCKED'
    : codes.includes('IMAGE_GENERATION_NO_IMAGE') ? 'IMAGE_GENERATION_NO_IMAGE' : undefined;
}

export function isTerminalImageGenerationFailure(error: unknown): boolean {
  return terminalImageGenerationFailureCode(error) !== undefined;
}

/** Accept only product-owned phrases, never arbitrary exception/provider text. */
export function personaSheetFailureMessage(message: unknown): string {
  const known = Object.values(messages).find(value => value === message);
  return `${known ?? 'Character sheet could not be generated because of a technical error. Retry explicitly.'} Your original photos are unchanged.`;
}

export function personaSceneFailureMessage(message: unknown): string {
  return Object.values(messages).find(value => value === message) ?? 'Nano Banana persona scene generation failed.';
}

/** Read image bytes without dereferencing malformed provider candidates or parts. */
export function geminiImageUrls(response: unknown): string[] {
  const candidates = record(response).candidates;
  if (!Array.isArray(candidates)) return [];
  return candidates.flatMap(candidate => {
    const parts = record(record(candidate).content).parts;
    if (!Array.isArray(parts)) return [];
    return parts.flatMap(part => {
      const data = record(record(part).inlineData);
      return typeof data.data === 'string' && data.data && typeof data.mimeType === 'string' && data.mimeType.startsWith('image/')
        ? [`data:${data.mimeType};base64,${data.data}`] : [];
    });
  });
}
